import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Card, SectionHeader } from "./ui";

const QUOTE_STATUS = ["envoye", "accepte", "refuse"];
const QS_LABELS = { envoye: "Envoyé", accepte: "Accepté", refuse: "Refusé" };
const QS_COLORS = { envoye: "bg-sky-100 text-sky-700", accepte: "bg-emerald-100 text-emerald-700", refuse: "bg-rose-100 text-rose-700" };
const FILTERS = [["all", "Tous"], ["envoye", "Envoyé"], ["accepte", "Accepté"], ["refuse", "Refusé"]];

export default function QuotesAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [delay, setDelay] = useState("");
  const load = useCallback(() => {
    api.get("/admin/reservations").then((r) => setItems(r.data.filter((x) => x.formule))).catch(() => {});
  }, []);
  useEffect(() => { load(); api.get("/admin/settings").then((r) => setDelay(String(r.data.reminder_delay_days))).catch(() => {}); }, [load]);

  const setStatus = async (r, status) => {
    try { await api.patch(`/admin/reservations/${r.id}/quote-status`, { status }); toast.success("Statut du devis mis à jour"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const sendDevis = async (r) => {
    try { await api.post(`/admin/reservations/${r.id}/send-devis`); toast.success("Devis envoyé à l'enseignant"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const saveDelay = async () => {
    const n = parseInt(delay, 10);
    if (!n || n < 1) { toast.error("Délai invalide"); return; }
    try { await api.patch("/admin/settings", { reminder_delay_days: n }); toast.success(`Relance après ${n} jours`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/quotes/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "devis-kwarizmix.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-BE", { day: "numeric", month: "short", year: "numeric" }) : "-");

  const filtered = filter === "all" ? items : items.filter((r) => (r.quote_status || "envoye") === filter);

  return (
    <Card>
      <SectionHeader
        title="Suivi des devis"
        action={<button onClick={exportCsv} data-testid="quotes-export-csv" className="text-sm font-bold px-4 py-2 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors">Exporter (CSV)</button>}
      />
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-2xl bg-slate-50" data-testid="quotes-reminder-setting">
        <span className="text-sm font-semibold text-slate-700">Relance automatique des devis « Envoyé » après</span>
        <Input type="number" min="1" value={delay} onChange={(e) => setDelay(e.target.value)} data-testid="reminder-delay-input" className="w-20 h-9 rounded-xl text-center" />
        <span className="text-sm text-slate-600">jours sans réponse</span>
        <button onClick={saveDelay} data-testid="reminder-delay-save" className="text-xs font-bold px-3 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-700 transition-colors">Enregistrer</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="quotes-status-filter">
        {FILTERS.map(([val, label]) => {
          const count = val === "all" ? items.length : items.filter((r) => (r.quote_status || "envoye") === val).length;
          return (
            <button
              key={val}
              onClick={() => setFilter(val)}
              data-testid={`quotes-filter-${val}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${filter === val ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              {label} <span className="opacity-70">({count})</span>
            </button>
          );
        })}
      </div>
      {filtered.length === 0 ? <p className="text-slate-500">Aucun devis pour ce filtre.</p> : (
        <div className="overflow-x-auto" data-testid="admin-quotes-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="py-3 pr-3">Réf.</th>
                <th className="py-3 pr-3">École / Contact</th>
                <th className="py-3 pr-3">Atelier</th>
                <th className="py-3 pr-3">Date</th>
                <th className="py-3 pr-3">Total</th>
                <th className="py-3 pr-3">Statut</th>
                <th className="py-3 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-slate-50" data-testid={`quote-row-${r.id}`}>
                  <td className="py-3 pr-3 font-mono font-bold text-slate-700">{r.quote_ref || "-"}</td>
                  <td className="py-3 pr-3">
                    <p className="font-semibold text-slate-800">{r.school || "-"}</p>
                    <p className="text-xs text-slate-400">{r.contact_name} · {r.email}</p>
                  </td>
                  <td className="py-3 pr-3 text-slate-600">{r.item_title}</td>
                  <td className="py-3 pr-3 text-slate-500">{fmt(r.created_at)}</td>
                  <td className="py-3 pr-3 font-bold text-slate-900">{r.estimated_total != null ? `${r.estimated_total} €` : "sur devis"}</td>
                  <td className="py-3 pr-3">
                    <div className="flex flex-col gap-1">
                      <span className={`text-xs font-bold px-2 py-1 rounded-full w-fit ${QS_COLORS[r.quote_status || "envoye"]}`}>{QS_LABELS[r.quote_status || "envoye"]}</span>
                      {r.quote_reminded && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full w-fit bg-amber-100 text-amber-700" data-testid={`quote-reminded-${r.id}`}>Relancé</span>}
                    </div>
                  </td>
                  <td className="py-3 pr-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <a href={`${api.defaults.baseURL}/reservations/${r.id}/devis.pdf`} target="_blank" rel="noreferrer" data-testid={`quote-pdf-${r.id}`} className="text-xs font-bold px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">PDF</a>
                      <button onClick={() => sendDevis(r)} data-testid={`quote-send-${r.id}`} className="text-xs font-bold px-3 py-1.5 rounded-full bg-sky-500 text-white hover:bg-sky-600 transition-colors">Renvoyer</button>
                      <Select value={r.quote_status || "envoye"} onValueChange={(v) => setStatus(r, v)}>
                        <SelectTrigger className="rounded-xl w-32 h-8" data-testid={`quote-status-${r.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{QUOTE_STATUS.map((s) => <SelectItem key={s} value={s}>{QS_LABELS[s]}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
